import React from "react";

import { ArrowLeftIcon, ChevronRightIcon } from "lucide-react";
import { INTEGRATION_STATUSES } from "constants/statuses";

import { Box, Stack, Typography, useTheme } from "@mui/material";

import { Image } from "@repo/ui";
import { StatusChip } from "components/display/StatusChip";

// Shared building blocks for the "hub" method-chooser flow used by both the products
// Add modal (`AddModal.tsx`) and the owners Add modal (`AddOwnerModal.tsx`), so the two
// can't drift (VRG-78: hoist the catalog + rows into one shared constant).

// Ecommerce integration catalog. Mirrors the catalog that landed on the Settings →
// Installation page (`installs` drives "most popular" ordering; `available` gates
// connectable vs "Coming soon"). `installs` are MOCK today — the real catalog +
// counts come from the Integrations table (TODO(VERA-405)).
export interface EcommerceIntegration {
  id: string;
  name: string;
  logo: string;
  description: string;
  available: boolean;
  installs: number;
}

export const ECOMMERCE_INTEGRATIONS: EcommerceIntegration[] = [
  {
    id: "shopify",
    name: "Shopify",
    logo: "https://cdn.shopify.com/static/images/logos/shopify-bag.png",
    description: "Sync products, orders, and customers from your Shopify store.",
    available: true,
    installs: 4200,
  },
  // Not yet wired — shown as "Coming soon" (TODO(VERA-405)).
  {
    id: "woocommerce",
    name: "WooCommerce",
    logo: "https://www.vectorlogo.zone/logos/woocommerce/woocommerce-icon.svg",
    description: "Import products and orders from your WooCommerce store.",
    available: false,
    installs: 3100,
  },
  {
    id: "webflow",
    name: "Webflow",
    logo: "https://www.vectorlogo.zone/logos/webflow/webflow-icon.svg",
    description: "Import products from your Webflow site.",
    available: false,
    installs: 1800,
  },
  {
    id: "wix",
    name: "Wix",
    logo: "https://www.vectorlogo.zone/logos/wix/wix-icon.svg",
    description: "Import products from your Wix store.",
    available: false,
    installs: 1400,
  },
  {
    id: "squarespace",
    name: "Squarespace",
    logo: "https://www.vectorlogo.zone/logos/squarespace/squarespace-icon.svg",
    description: "Import products from your Squarespace commerce store.",
    available: false,
    installs: 1100,
  },
  {
    id: "bigcommerce",
    name: "BigCommerce",
    logo: "https://www.vectorlogo.zone/logos/bigcommerce/bigcommerce-icon.svg",
    description: "Sync your BigCommerce catalog and customers.",
    available: false,
    installs: 800,
  },
  {
    id: "magento",
    name: "Magento",
    logo: "https://www.vectorlogo.zone/logos/magento/magento-icon.svg",
    description: "Import your Magento store catalog.",
    available: false,
    installs: 500,
  },
];

// Most popular first; show a few by default + reveal the rest (mirrors Installation).
export const DEFAULT_VISIBLE_INTEGRATIONS = 3;

export interface MethodOptionProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  /**
   * Rendered between the description and the chevron — today, the "Connected" status chip on
   * the store row (VERA-455).
   *
   * A SLOT rather than a `connected` boolean: this row is shared by the products and owners Add
   * modals, and "connected" is meaningless to the second. The chevron stays regardless, because
   * the row still navigates — the chip states a fact about the destination, it is not a
   * substitute for the affordance (unlike `IntegrationRow`, where a connected row is inert and
   * the chip legitimately replaces the chevron).
   */
  trailing?: React.ReactNode;
  onClick: () => void;
}

// A large tappable choice row: icon + title + one-line description + chevron.
// Shares the field border treatment (primary[100] rest, brand[600] + ring on
// hover/focus) so it reads as part of the design system.
export const MethodOption = ({ icon, title, description, trailing, onClick }: MethodOptionProps) => {
  const theme = useTheme();
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 3,
        p: 4,
        cursor: "pointer",
        borderRadius: "8px",
        border: `1px solid ${theme.palette.primary[100]}`,
        backgroundColor: theme.palette.background.paper,
        transition: "border-color 120ms ease, box-shadow 120ms ease",
        "&:hover, &:focus-visible": {
          borderColor: theme.palette.brand[600],
          boxShadow: `0 0 0 3px ${theme.palette.brand["600-6%"]}`,
          outline: "none",
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 40,
          height: 40,
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.primary[50],
        }}
      >
        {icon}
      </Box>
      <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {title}
        </Typography>
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {description}
        </Typography>
      </Stack>
      {trailing}
      <ChevronRightIcon strokeWidth={1} size={20} color={theme.palette.text.secondary} />
    </Box>
  );
};

// Back navigation row shown at the top of the "manual" and "integration" screens,
// returning to the hub method-chooser.
export const BackRow = ({ onBack }: { onBack: () => void }) => (
  <Box
    role="button"
    tabIndex={0}
    onClick={onBack}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onBack();
      }
    }}
    sx={{
      display: "inline-flex",
      alignSelf: "flex-start",
      alignItems: "center",
      gap: 1,
      cursor: "pointer",
      color: "text.secondary",
      "&:hover, &:focus-visible": { color: "text.primary", outline: "none" },
    }}
  >
    <ArrowLeftIcon strokeWidth={1} size={18} />
    <Typography variant="body3">Back</Typography>
  </Box>
);

export interface IntegrationRowProps {
  integration: EcommerceIntegration;
  connected: boolean;
  onClick: () => void;
}

// An ecommerce integration in the "Connect a store" list — logo + name + description,
// mirroring the Settings → Installation rows. Connectable rows use the action-row
// treatment (border + ring on hover/focus); "Coming soon" rows are inert.
export const IntegrationRow = ({ integration, connected, onClick }: IntegrationRowProps) => {
  const theme = useTheme();
  const interactive = integration.available && !connected;
  return (
    <Box
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? onClick : undefined}
      onKeyDown={(e) => {
        if (interactive && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 3,
        p: 4,
        borderRadius: "8px",
        border: `1px solid ${theme.palette.primary[100]}`,
        backgroundColor: theme.palette.background.paper,
        opacity: integration.available ? 1 : 0.6,
        ...(interactive && {
          cursor: "pointer",
          transition: "border-color 120ms ease, box-shadow 120ms ease",
          "&:hover, &:focus-visible": {
            borderColor: theme.palette.brand[600],
            boxShadow: `0 0 0 3px ${theme.palette.brand["600-6%"]}`,
            outline: "none",
          },
        }),
      }}
    >
      <Image
        src={integration.logo}
        alt={integration.name}
        fit="cover"
        sx={{
          flexShrink: 0,
          borderRadius: "8px",
          p: 1,
          border: `1px solid ${theme.palette.grey[200]}`,
          minWidth: 40,
          maxWidth: 40,
          height: 40,
        }}
      />
      <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {integration.name}
        </Typography>
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {integration.description}
        </Typography>
      </Stack>
      {connected ? (
        <StatusChip statuses={INTEGRATION_STATUSES} status="connected" size="small" />
      ) : !integration.available ? (
        <Typography variant="body3" color="text.secondary" sx={{ flexShrink: 0 }}>
          Coming soon
        </Typography>
      ) : (
        <ChevronRightIcon strokeWidth={1} size={20} color={theme.palette.text.secondary} />
      )}
    </Box>
  );
};
